import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError, eur, minimoMsg } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SLIDER_MAX, sliderMinFor } from "@/lib/constants";
import { SinistriDeclaration, SINISTRI_YEARS } from "@/components/SinistriDeclaration";
import { IddDeclaration } from "@/components/IddDeclaration";
import { CatNatUbicazione, emptyLoc } from "@/components/CatNatUbicazione";
import { Calculator, ShieldCheck, AlertTriangle, XCircle, Wallet, Save, Loader2, Lock, Mountain, Plus } from "lucide-react";

const UW_BOOLS = [
  ["impresa_agricola_primaria", "Impresa agricola con attività primaria (art. 2135 c.c.)"],
  ["sostituzione_altra_compagnia", "Sostituzione di polizza in essere con altra compagnia"],
  ["sinistri_pregressi", "Sinistri negli ultimi 3 anni (di qualsiasi tipo)"],
  ["incendio_assimoco", "Il cliente ha una polizza Incendio Assimoco (necessaria per assicurare le Merci)"],
];
const MAX_UB = 5;
const toLoc = (i) => ({ ...emptyLoc(), ...Object.fromEntries(Object.keys(emptyLoc()).filter((k) => i[k] != null).map((k) => [k, i[k]])),
  partite: { ...emptyLoc().partite, ...(i.partite || {}) } });

export default function CatNatQuoteEngine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const requestId = params.get("request");
  const editId = params.get("edit");
  const { isAdmin, user } = useAuth();
  const sliderMin = sliderMinFor(user?.role);
  const [editLoaded, setEditLoaded] = useState(false);

  const [locs, setLocs] = useState([emptyLoc()]);
  const [attivita, setAttivita] = useState("");
  const [eventi, setEventi] = useState({ terremoto: true, inondazione: true, franamento: true });
  const [scoperto, setScoperto] = useState(15);
  const [slider, setSlider] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sinistri, setSinistri] = useState(null);
  const [idd, setIdd] = useState(null);
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  const updLoc = (i) => (v) => setLocs((ls) => ls.map((l, k) => (k === i ? (typeof v === "function" ? v(l) : v) : l)));

  useEffect(() => {
    api.get("/catnat/config").then(({ data }) => setConfig(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data));
    if (params.get("from") === "check") toast.info("Garanzie obbligatorie pre-selezionate dalla verifica obbligo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-compila la 1ª ubicazione dall'anagrafica selezionata (solo nuovo preventivo)
  useEffect(() => {
    if (!anagraficaId || editId) return;
    const a = anagrafiche.find((x) => x.id === anagraficaId);
    if (a) updLoc(0)((l) => (l.comune || l.provincia ? l : { ...l, indirizzo: a.indirizzo || "", provincia: (a.provincia || "").toUpperCase(), comune: a.comune || "", cap: a.cap || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anagraficaId, anagrafiche]);

  useEffect(() => {
    if (!config || !editId || editLoaded) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.catnat_input || {};
      setAnagraficaId(q.anagrafica_id || "");
      setLocs(i.ubicazioni?.length ? i.ubicazioni.map(toLoc) : [toLoc(i)]);
      setAttivita(i.attivita || "");
      if (i.eventi) setEventi(i.eventi);
      if (i.scoperto != null) setScoperto(i.scoperto);
      setSlider(isAdmin ? i.slider_pct || 0 : 0);
      setAnswers(i.underwriting_answers || {});
      if (i.data_effetto) setDataEffetto(i.data_effetto);
      if (q.sinistri) setSinistri(q.sinistri);
      if (q.idd) setIdd(q.idd);
      setEditLoaded(true);
      toast.info("Modalità modifica: aggiorna i dati e salva per ricalcolare");
    }).catch(() => setEditLoaded(true));
  }, [config, editId, editLoaded, isAdmin]);

  const buildPayload = () => {
    const ub = locs.map((l) => ({ ...l, provincia: (l.provincia || "").trim().toUpperCase(), zona_sismica: l.zona_sismica || null,
      partite: Object.fromEntries(Object.entries(l.partite).map(([k, v]) => [k, Number(v || 0)])) }));
    return { ...ub[0], ubicazioni: ub, attivita, eventi, scoperto: Number(scoperto), slider_pct: isAdmin ? slider : 0,
      data_effetto: dataEffetto, underwriting_answers: answers, sinistri, idd };
  };

  const calculate = async () => {
    setCalculating(true);
    try { const { data } = await api.post("/catnat/quotes/calculate", buildPayload()); setResult(data); }
    catch (e) { toast.error(apiError(e)); } finally { setCalculating(false); }
  };
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona un'anagrafica");
    setSaving(true);
    try {
      const { data } = await api.post("/catnat/quotes", { ...buildPayload(), anagrafica_id: anagraficaId, request_id: requestId, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato" : "Preventivo salvato");
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const totaleSomme = useMemo(() => locs.reduce((s, l) => s + Object.values(l.partite).reduce((a, v) => a + Number(v || 0), 0), 0), [locs]);
  const sommeLimite = useMemo(() => locs.reduce((s, l) => s + ["fabbricato", "macchinario", "terreni"].reduce((a, k) => a + Number(l.partite[k] || 0), 0), 0), [locs]);
  const limiteAuto = sommeLimite <= 1000000 ? 100 : 70;

  const statusBanner = useMemo(() => {
    if (!result) return null;
    if (result.status === "non_ammissibile")
      return { icon: XCircle, tone: "bg-red-50 border-red-200 text-red-800", title: "Rischio non assumibile", msgs: result.eligibility.blocking_errors };
    if (result.binario === "referral_tecnico")
      return { icon: AlertTriangle, tone: "bg-amber-50 border-amber-200 text-amber-800", title: "Referral tecnico richiesto",
        msgs: [...(result.eligibility.warnings || []), "Emissione autonoma bloccata: invia all'Agenzia."] };
    return { icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Preventivo standard — emettibile", msgs: [...(result.eligibility.warnings || []), ...minimoMsg(result)] };
  }, [result]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  const q = [anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&");
  const qs = q ? `?${q}` : "";
  const premium = result?.premium;
  const multi = locs.length > 1;

  return (
    <div className="space-y-6">
      <div>
        <div className={`mb-3 flex flex-wrap gap-2 ${editId ? "hidden" : ""}`} data-testid="product-switch">
          <Link to={`/quota/nuovo${qs}`} data-testid="switch-to-rc" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">RC Economia Solidale</Link>
          <Link to={`/quota/mrv${qs}`} data-testid="switch-to-mrv" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Tutela Multirischi Volontariato</Link>
          <Link to={`/quota/infortuni${qs}`} data-testid="switch-to-inf" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Infortuni Economia Solidale</Link>
          <span className="flex items-center gap-1.5 rounded-full border border-forest bg-forest px-4 py-1.5 text-sm font-medium text-white"><Mountain className="h-4 w-4" /> CAT NAT Assimoco</span>
          <Link to={`/quota/multirischi${qs}`} data-testid="switch-to-multirischi" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Multirischi Danni ai Beni</Link>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Preventivo — CAT NAT Assimoco</h1>
        <p className="mt-1 text-muted-foreground">Norme 2CE Ed. 03/2025 · Eventi catastrofali ex L. 213/2023 · premio minimo {eur(config.min_premio_polizza)} imponibile · fino a {MAX_UB} ubicazioni.</p>
        <div data-testid="catnat-sardegna-note" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Quotatore valido <b>solo per il territorio della Sardegna</b>. Per rischi fuori Sardegna invia i dati via e-mail a <a href="mailto:agenzia@assietica.it" className="font-medium underline">agenzia@assietica.it</a>.</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 lg:col-span-3">
          <Card className="rounded-xl border-border/60 p-5">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Anagrafica cliente</Label>
                <Select value={anagraficaId} onValueChange={setAnagraficaId}>
                  <SelectTrigger data-testid="catnat-anagrafica" className="[&>span]:truncate"><SelectValue placeholder="Seleziona ente..." /></SelectTrigger>
                  <SelectContent>{anagrafiche.map((a) => <SelectItem key={a.id} value={a.id}>{a.ragione_sociale}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Attività esercitata</Label>
                <Input data-testid="catnat-attivita" value={attivita} onChange={(e) => setAttivita(e.target.value)} placeholder="es. Cooperativa di servizi alle persone" /></div>
            </div>
          </Card>

          {locs.map((l, i) => (
            <CatNatUbicazione key={i} i={i} loc={l} config={config} multi={multi} onChange={updLoc(i)}
              onRemove={multi ? () => setLocs((ls) => ls.filter((_, k) => k !== i)) : null} />
          ))}
          {locs.length < MAX_UB && (
            <button onClick={() => setLocs((ls) => [...ls, emptyLoc()])} data-testid="catnat-add-ubicazione"
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-forest/30 py-3 text-sm font-semibold text-forest transition-colors hover:bg-sage">
              <Plus className="h-4 w-4" />Aggiungi ubicazione ({locs.length}/{MAX_UB})
            </button>
          )}

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Eventi catastrofali, scoperto e limite</h2>
            <div className="space-y-3">
              {config.eventi.map((e) => (
                <label key={e.code} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm">
                  <Checkbox data-testid={`catnat-evento-${e.code}`} checked={!!eventi[e.code]} onCheckedChange={(v) => setEventi({ ...eventi, [e.code]: !!v })} />
                  <span className="text-forest">{e.label}</span>
                </label>
              ))}
              <div className="space-y-1.5"><Label>Scoperto</Label>
                <Select value={String(scoperto)} onValueChange={(v) => setScoperto(Number(v))}>
                  <SelectTrigger data-testid="catnat-scoperto" className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.scoperti.map((s) => <SelectItem key={s} value={String(s)}>{s}%</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="rounded-lg bg-sage/60 px-3 py-2.5 text-sm text-forest" data-testid="catnat-limite-auto">
                Limite di indennizzo <b>{limiteAuto}%</b>: somme Fabbricato, Macchinario e Terreni {eur(sommeLimite)} (100% fino a 1 milione, 70% fino a 30 milioni). Totale Sezione {eur(totaleSomme)} / max {eur(config.limite_sezione)}.
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Questionario assuntivo</h2>
            <div className="space-y-2.5">
              {UW_BOOLS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm">
                  <Checkbox data-testid={`catnat-uw-${key}`} checked={!!answers[key]} onCheckedChange={(v) => setAnswers({ ...answers, [key]: !!v })} />
                  <span className="text-forest">{label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 border-t border-border/60 pt-4">
              <SinistriDeclaration value={sinistri} onChange={setSinistri} periodoAnni={SINISTRI_YEARS.cat_nat_impresa} testidPrefix="catnat-sinistri" />
              <div className="mt-4 border-t border-border/60 pt-4">
                <p className="mb-2 text-sm font-semibold text-forest">Valutazione di adeguatezza (IDD)</p>
                <IddDeclaration value={idd} onChange={setIdd} testidPrefix="catnat-idd" />
              </div>
            </div>
          </Card>
        </div>

        <div className="min-w-0 space-y-5 lg:col-span-2">
          <Card className="sticky top-24 rounded-xl border-border/60 p-5">
            {isAdmin ? (
              <>
                <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Flessibilità (riservata all'Agenzia)</h2>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">{slider < 0 ? "Sconto" : "Aumento"}</span>
                  <span data-testid="catnat-slider-value" className={`font-heading text-2xl font-semibold ${slider < 0 ? "text-terracotta" : "text-forest"}`}>{slider > 0 ? "+" : ""}{slider}%</span>
                </div>
                <Slider data-testid="catnat-slider" className="mt-3" value={[slider]} min={sliderMin} max={SLIDER_MAX} step={5} onValueChange={(v) => setSlider(v[0])} />
                <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>{sliderMin}%</span><span>+{SLIDER_MAX}%</span></div>
              </>
            ) : (
              <p className="flex gap-2 text-xs text-muted-foreground" data-testid="catnat-flex-note"><Lock className="h-4 w-4 shrink-0" />Premio di tariffa: sconti e aumenti sono riservati all'Agenzia (Norme 2CE).</p>
            )}

            <div className="mt-4 space-y-1.5"><Label>Data di effetto</Label>
              <Input data-testid="catnat-data-effetto" type="date" value={dataEffetto} onChange={(e) => setDataEffetto(e.target.value)} /></div>

            <Button data-testid="catnat-calculate-button" onClick={calculate} disabled={calculating || totaleSomme <= 0}
              className="mt-5 h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
              {calculating ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Calculator className="h-5 w-5" /> Calcola premio</>}
            </Button>

            {result && (
              <div className="mt-5 space-y-4" data-testid="catnat-quote-result">
                {statusBanner && (
                  <div className={`flex gap-2 rounded-lg border p-3 text-sm ${statusBanner.tone}`}>
                    <statusBanner.icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div><div className="font-medium">{statusBanner.title}</div>
                      {statusBanner.msgs.map((m, i) => <div key={i} className="mt-0.5 text-xs opacity-90">{m}</div>)}</div>
                  </div>
                )}
                {premium && (
                  <div className="rounded-lg bg-sage/50 p-4">
                    {premium.breakdown.map((b, i) => (
                      <div key={i} className="flex justify-between gap-2 py-1 text-xs">
                        <span className="text-muted-foreground">{premium.n_ubicazioni > 1 && `Ub. ${b.ubicazione} · `}{b.evento_label.split(",")[0]} — {b.partita_label.split(",")[0]}{b.min_applied && <span className="ml-1 text-amber-600">(min)</span>}</span>
                        <span className="font-medium text-forest">{eur(b.gross)}</span>
                      </div>
                    ))}
                    <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2">
                      <span className="font-medium text-forest">Premio annuo lordo</span>
                      <span data-testid="catnat-premio-finale" className="font-heading text-2xl font-semibold text-forest">{eur(premium.gross_total)}</span>
                    </div>
                    {premium.minimum_premium_applied && <div className="mt-1 text-xs text-amber-700">Applicato premio minimo di polizza.</div>}
                    <div className="mt-1 text-xs text-muted-foreground" data-testid="catnat-frazionamenti">
                      Limite indennizzo {premium.limite_indennizzo}% · Rate ammesse: annuale{premium.frazionamenti?.semestrale ? ", semestrale" : ""}{premium.frazionamenti?.mensile ? ", mensile" : ""}
                    </div>
                  </div>
                )}
                {result.provvigione && (
                  <div data-testid="catnat-commission-box" className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-terracotta"><Wallet className="h-3.5 w-3.5" /> Provvigione (solo per te)</div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">{result.provvigione.pct}% dell'imponibile</span>
                      <span className="font-heading text-xl font-semibold text-terracotta">{eur(result.provvigione.importo)}</span>
                    </div>
                  </div>
                )}
                {result.status !== "non_ammissibile" && (
                  <Button data-testid="catnat-save-quote-button" onClick={save} disabled={saving || !anagraficaId}
                    className="h-12 w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" /> Salva preventivo</>}
                  </Button>
                )}
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Le provvigioni non compaiono mai nella vista cliente.</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
